"use client"

import { useCallback, useState } from "react"
import { logClientError } from "~/lib/logger-client"

import { PurchaseLegalMicrocopy } from "~/components/ads-framework/PurchaseLegalMicrocopy"
import { InAppBrowserHint } from "~/components/shared/InAppBrowserHint"
import { attributionSnapshotForCheckout } from "~/lib/analytics/attribution"
import { analyticsConsentSignal, adSaleShareDenied } from "~/lib/analytics/consent"
import { GA_MEASUREMENT_ID } from "~/lib/analytics/ga-config"
import { gaClientIdFromCookie, gaSessionFromCookie } from "~/lib/analytics/ga-client-id"
import {
  trackBeginCheckout,
  trackCheckoutError,
} from "~/lib/ads-framework/storefront-analytics"
import type { CheckoutAvailability } from "~/server/stripe/availability"

interface BuyButtonProps {
  /** Optional class names. */
  className?: string
  children?: React.ReactNode
  /**
   * Which SKU this button checks out. Defaults to `ads-framework` so
   * existing callers don't need to change. The others: the zip-only
   * `ads-skills-only` / `email-skills-only` bundles, and the full
   * `email-framework`.
   */
  product?: "ads-framework" | "ads-skills-only" | "email-framework" | "email-skills-only"
  /**
   * Server-computed checkout availability. When not `"operational"`,
   * the button renders a clear "checkout unavailable on this deploy"
   * status pill instead of attempting a /api/checkout call that's
   * guaranteed to fail (e.g. test-mode key on a preview deploy where
   * the live-mode price IDs can't be resolved).
   *
   * Defaults to `"operational"` for backwards compat — existing test
   * harnesses that mount `<BuyButton />` without the prop continue to
   * exercise the active codepath.
   */
  availability?: CheckoutAvailability
  /**
   * Where on the page this CTA lives (e.g. `"hero"`, `"pricing"`,
   * `"final_cta"`). Threaded into the checkout analytics so funnel
   * drop-off can be attributed to the specific placement a buyer used.
   * Optional — omitting it just leaves the dimension unset.
   */
  placement?: string
  /**
   * List price of this SKU in whole USD, forwarded to the `begin_checkout`
   * analytics `value` so GA4 Monetization reports carry a funnel value.
   * Intent-stage list price (pre-promo); omitting it leaves `value` unset.
   */
  priceUsd?: number
  /**
   * When true (default), render Terms/Returns/Shipping links below the CTA.
   * Merchant Center expects policies within one click of the purchase action.
   */
  showLegalLinks?: boolean
}

const SUPPORT_EMAIL = "support@adsights.ai"

type BuyButtonProduct = NonNullable<BuyButtonProps["product"]>

/**
 * Landing-page path per SKU. Used by the error/preview-unavailable states so a
 * buyer on the EMAIL product pages is pointed back at the email surface, not
 * the ads one (the component is shared across both verticals via the `product`
 * prop). Exhaustive over the SKU union so a new SKU is a compile error here.
 */
const LANDING_PATH_BY_PRODUCT: Record<BuyButtonProduct, string> = {
  "ads-framework": "/frameworks/ads-framework",
  "ads-skills-only": "/frameworks/ads-skills",
  "email-framework": "/frameworks/email-framework",
  "email-skills-only": "/frameworks/email-skills",
}

/**
 * Posts to /api/checkout, then redirects the browser to the Stripe
 * Checkout URL. Server captures the buyer's email + GitHub username via
 * Stripe Checkout (custom_fields) so no form is required on our side —
 * one click → Stripe Checkout.
 *
 * Error UX (#31): server-side configuration errors (e.g. missing
 * STRIPE_SECRET_KEY on a preview deploy) used to surface verbatim to
 * buyers. We now collapse the entire failure surface to a single generic
 * message + a mailto link so paying buyers have a recovery path that
 * doesn't leak operational state. The detailed error is preserved in the
 * browser console so dev-tools-savvy users can self-report cleanly.
 */
export function BuyButton({
  className,
  children,
  product,
  availability = "operational",
  placement,
  priceUsd,
  showLegalLinks = true,
}: BuyButtonProps): React.ReactElement {
  // Normalize to the SKU the checkout helper records (defaults to
  // ads-framework, matching the /api/checkout server default).
  const sku = product ?? "ads-framework"
  const [loading, setLoading] = useState(false)
  const [errored, setErrored] = useState(false)

  // Resolve the product's own landing path for the error/preview states so
  // they reference the surface the buyer is actually on (defaults to ads for
  // legacy callers that omit `product`).
  const landingPath = LANDING_PATH_BY_PRODUCT[product ?? "ads-framework"]

  // useCallback keeps the handler reference stable across re-renders so the
  // button doesn't appear "new" to React DevTools / memo'd parents and
  // satisfies react/jsx-no-bind. Declared unconditionally (above the
  // availability early-return) to satisfy react-hooks/rules-of-hooks —
  // hooks must run in the same order on every render.
  const onClick = useCallback(async (): Promise<void> => {
    setLoading(true)
    setErrored(false)
    try {
      // Bounded-await so begin_checkout dispatches before a fast Stripe redirect
      // on first-click / no-cookie sessions (attribution hydrates inside).
      await trackBeginCheckout({ product: sku, placement, valueUsd: priceUsd }).catch(() => {
        // ignore
      })
      // Without an email collected ahead of time, Stripe Checkout itself
      // prompts the buyer for one. We can also collect email + github
      // username upfront via a modal; v1 routes straight to Stripe to
      // minimize friction.
      // Carry analytics signals into Checkout metadata so the server-side
      // purchase can attribute even if the client beacon is blocked. The CLIENT
      // can't know its own geo (by design — keeps the layout bfcache-eligible),
      // so it sends a tri-state consent SIGNAL and lets the server resolve the
      // region-aware decision: explicit opt-in (`true`) grants everywhere,
      // explicit opt-out/GPC (`false`) denies, and "no stored choice"
      // (`undefined`) is left for the server to resolve by region (rest-of-world
      // = granted, EEA/UK/CH = denied). We attach the client_id + snapshot unless
      // the buyer EXPLICITLY opted out; the server withholds them (and skips GA4)
      // when its region decision lands on denied.
      const consentSignal = analyticsConsentSignal() // true | false | undefined
      const explicitlyDenied = consentSignal === false
      const cookie = typeof document === "undefined" ? undefined : document.cookie
      const gaClientId = explicitlyDenied ? undefined : gaClientIdFromCookie(cookie)
      const attribution = explicitlyDenied ? undefined : attributionSnapshotForCheckout()
      // GA4 session id (+ number) from the per-stream `_ga_<container>` cookie, so
      // the webhook's server-side purchase inherits the buyer's GA4 SESSION
      // attribution (source/medium/campaign), not just the client_id user stitch.
      const gaSession = explicitlyDenied ? undefined : gaSessionFromCookie(cookie, GA_MEASUREMENT_ID)
      const body = {
        ...(product ? { product } : {}),
        ...(gaClientId ? { gaClientId } : {}),
        ...(gaSession ? { gaSessionId: gaSession.sessionId } : {}),
        ...(gaSession?.sessionNumber ? { gaSessionNumber: gaSession.sessionNumber } : {}),
        ...(attribution ? { attribution } : {}),
        ...(consentSignal === undefined ? {} : { analyticsConsent: consentSignal }),
        // Sale/share opt-out (GPC or explicit ad opt-out): the server-side
        // Measurement-Protocol purchase denies the ad signals. GPC still allows
        // first-party measurement (analytics), so this is separate from
        // `analyticsConsent`. Absent = granted (opt-out default).
        ...(adSaleShareDenied() ? { adSaleShareDenied: true } : {}),
      }
      const res = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string }
        // One structured entry to the client log drain for triage; never surfaced
        // to the buyer (could leak env-var names or stack traces).
        logClientError(new Error(`[BuyButton] /api/checkout failed (status ${res.status})`), {
          component: "BuyButton",
          context: "checkout failed",
          status: res.status,
          serverError: body.error ?? "(no body)",
        })
        trackCheckoutError({ product: sku, status: res.status, placement })
        throw new Error("CHECKOUT_FAILED")
      }
      const { url } = (await res.json()) as { url: string }
      window.location.assign(url)
    } catch (err) {
      // The non-OK branch already logged a structured, status-bearing entry
      // before throwing this sentinel; only log/track OTHER errors here (e.g. a
      // network throw before any response) so a failed checkout is ONE drain row.
      if (!(err instanceof Error && err.message === "CHECKOUT_FAILED")) {
        logClientError(err, { component: "BuyButton", context: "checkout error" })
        trackCheckoutError({ product: sku, placement })
      }
      setErrored(true)
      setLoading(false)
    }
  }, [product, sku, placement, priceUsd])

  // Non-operational deploys (preview with test-mode key / misconfigured
  // production) get a clear status pill instead of a clickable button.
  // No /api/checkout call is made — the user sees the reason up front
  // rather than after a failed POST.
  if (availability !== "operational") {
    return (
      <div className="inline-flex max-w-md flex-col items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-center text-sm">
        <p className="font-semibold text-amber-300">
          {availability === "preview"
            ? "Checkout is disabled on this preview deploy"
            : "Checkout is temporarily unavailable"}
        </p>
        <p className="text-foreground/80">
          {availability === "preview" ? (
            <>
              Previews run in Stripe test mode, but our product catalog only
              exists in live mode — so checkout would fail. Visit the live
              site at{" "}
              <a
                href={`https://www.adsights.ai${landingPath}`}
                className="font-medium underline"
              >
                adsights.ai
              </a>{" "}
              to purchase.
            </>
          ) : (
            <>
              Our storefront couldn&rsquo;t be reached. Email{" "}
              <a
                href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("Checkout unavailable")}`}
                className="font-medium underline"
              >
                {SUPPORT_EMAIL}
              </a>{" "}
              and we&rsquo;ll send a direct invoice link.
            </>
          )}
        </p>
      </div>
    )
  }

  return (
    // `items-center` (not items-start) so when the error banner appears
    // it's centered relative to the button rather than left-aligned —
    // the banner is wider than the button, so items-start used to make
    // the button shift visually left as soon as the error rendered,
    // which read as a layout glitch inside an otherwise-centered CTA
    // section.
    <div className="inline-flex flex-col items-center gap-2">
      <InAppBrowserHint context="checkout" className="max-w-md" />
      <button
        type="button"
        onClick={onClick}
        disabled={loading}
        className={
          className ??
          "rounded-md bg-primary px-6 py-3 text-base font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
        }
      >
        {loading ? "Redirecting to checkout…" : children ?? "Buy for $199"}
      </button>
      {errored ? (
        <div
          role="alert"
          className="max-w-md rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-center text-sm"
        >
          <p className="font-semibold text-destructive">Checkout couldn&rsquo;t start.</p>
          <p className="mt-1 text-foreground/80">
            We hit an issue handing off to Stripe — your card was not charged. Try once more,
            or email{" "}
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`Checkout failed on ${landingPath}`)}`}
              className="font-medium underline"
            >
              {SUPPORT_EMAIL}
            </a>{" "}
            and we&rsquo;ll send a direct invoice link.
          </p>
        </div>
      ) : null}
      {showLegalLinks ? <PurchaseLegalMicrocopy className="mt-2 max-w-md" /> : null}
    </div>
  )
}
